import { BoxProps } from '../layout/Box';
import { PostSource, PostStatus } from './constants';
import type { BusinessUI } from '../business/types';

export enum ClickoutTarget {
    BUTTON = 'button',
    GRID = 'grid',
}

export type ClickOut = {
    url: string;
    target: ClickoutTarget;
    text: string;
};

export enum ReactionState {
    LIKE = 'like',
    DISLIKE = 'dislike',
    NONE = 'none',
}

export type ContentUI =
    | {
          id: string;
          type: 'image';
          media: Inspire.Image[];
      }
    | {
          id: string;
          type: 'video';
          media: Inspire.Video[];
      };

export interface PriceUI {
    currency?: string;
    integerPart: number;
    fractionalPart?: number;
    formatted?: string;
    discountPercent?: number;
}

export interface FintechDiscountItemUI {
    discountPrice: number;
    discountPercent: number;
    maxDiscountAmount?: number;
    maxDiscountPercent?: number;
}

export interface FintechDiscountUI {
    yaPay?: FintechDiscountItemUI;
    yaSplit?: FintechDiscountItemUI;
}

export interface ProductItemUI {
    id: string;
    url: string;
    originalUrl: string;
    monetizationEnabled?: boolean;
    title: string;
    content: ContentUI[];
    priceInfo: PriceUI;
    oldPriceInfo: PriceUI;
    isUnavailable?: boolean;
    fintechDiscount?: FintechDiscountUI;
}

export interface ProductsUI {
    items: ProductItemUI[];
    buttonTitle: string;
    businessButtonTitle: string;
    description: string;
    title: string;
}

export interface OwnersDataUI {
    author: {
        id: string;
        displayName: string;
        avatarUrl: Inspire.Image[];
        shareLink: string;
    };
    shop: {
        displayUrl: string;
    };
    isOwner: boolean;
}

export interface PostCollectionUI {
    id: string;
    title: string;
    numberOfPosts: number;
    buttonText?: string;
    isSaved: boolean;
}

export enum DeclinedReason {
    NEED_PRODUCT = 'need_product',
}

export interface PostUI {
    id: string;
    url: string;
    shareUrl: string;
    views: number;
    likes: number;
    reactionState: ReactionState;
    saved: boolean;
    business?: BusinessUI;
    text: string;
    tags: string[];
    clickouts: ClickOut[];
    createdAt: string;
    status: PostStatus;
    declinedReason?: DeclinedReason;
    source: PostSource;
    djRequestId?: string;
    logContext?: string;
    content: ContentUI[];
    products: ProductsUI;
    postType?: 'post' | 'product' | 'collection';
    ownersData?: OwnersDataUI;
    adsData?: {
        showUrl?: string;
    };
    collectionData?: PostCollectionUI;
    /** Состояние тумблера продвижения поста. */
    enabledDefaultBoosting?: boolean;
    stats?: PostStatUI;
    publicStats?: {
        shows?: number;
        likes?: number;
    };
    monetizationEnabled?: boolean;
    isAIContent?: boolean;
}

export interface AdCardUI {
    isAd: true;
    id: string;
}

export type PostPersistenceMediaUI =
    | {
          id: string;
          url: string;
          width: number;
          height: number;
          type: 'image';
      }
    | {
          id: string;
          url: string;
          width: number;
          height: number;
          firstFrameUrl?: string;
          type: 'video';
      };

export interface PostPersistenceUI {
    id?: string;
    businessId?: string;
    text: string;
    site?: string;
    status?: PostStatus;
    source?: PostSource;
    media: PostPersistenceMediaUI[];
    products: ProductsUI;
    declinedReason?: DeclinedReason;
    needCpaRegistration?: boolean;
    isAIContent?: boolean;
}

export interface PostStatItemUI {
    value: number;
    display: string;
}

export interface PostStatUI {
    coverage?: {
        total: PostStatItemUI;
        subscribers: PostStatItemUI;
        notsubscribers: PostStatItemUI;
    };
    involved?: {
        total: PostStatItemUI;
        subscribers: PostStatItemUI;
        notsubscribers: PostStatItemUI;
    };
    shows?: {
        total: PostStatItemUI;
        subscribers: PostStatItemUI;
        notsubscribers: PostStatItemUI;
    };
    showsInGrid?: {
        total: PostStatItemUI;
        subscribers: PostStatItemUI;
        notsubscribers: PostStatItemUI;
    };
    subscribers?: {
        total: PostStatItemUI;
        subscribed: PostStatItemUI;
        unsubscribed: PostStatItemUI;
    };
    interactions?: {
        total: PostStatItemUI;
        clicks: PostStatItemUI;
        likes: PostStatItemUI;
        shares: PostStatItemUI;
        saves: PostStatItemUI;
    };
}

export interface RenderStatusElementProps extends BoxProps {
    status: PostUI['status'];
    declinedReason: PostUI['declinedReason'];
    monetizationEnabled: PostUI['monetizationEnabled'];
}
